import Sprite from 'Prototype/Sprite';
import Armature from 'Prototype/Armature';
import {
    FarmSlotTypeLayoutMap,
    getFullFarmItemAssetDetails,
    getSlotArmatureName,
    parseFarmSlotId,
} from 'System/Farm/FarmHelpers';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Renderable from 'Component/Renderable';
import { IGameState } from 'State/Game/IGameState';
import Game from 'Game';
import Interactable from 'Component/Interactable';
import { FarmSlotId } from 'State/Player/Farm/IFarmPlayerState';
import { UIActions } from 'State/UI/UIActions';
import { PopupID } from 'State/UI/PopupID';
import { FarmSlotType } from 'System/Farm/Data/FarmSlotType';
import { Anim, StartAnimationClip, StopAnimation } from 'System/Animation/AnimationHelper';
import { PlaySound } from 'System/Audio/AudioHelper';
import { DragonbonesFadeIn } from 'System/Animation/Clip';
import { Easing } from '@tweenjs/tween.js';
import TransformComponent from 'Component/Transform';
import { createSelector, OutputSelector } from 'reselect';
import Point from 'Utility/Point';
import {
    FarmItemId,
    FarmItemType,
    getFarmItemByID,
    getFarmItemLayout,
    isFarmItemId,
} from 'System/Farm/Data/FarmItemData';

export interface IFarmSlotData {
    slotId: string;
    areaId: number;
    selectNextSlot: (_direction: 'left' | 'right') => void;
    mapLayout: any;
    loaderRef: PIXI.loaders.Loader;
}

export class FarmSlot extends Sprite {
    public layout: any;
    private itemLayout: any; // TODO: GameObject types not supported in new Layouts editor version.
    private type: FarmSlotType;

    /**
     * TODO: Remove this and use state bindings.
     * @deprecated
     * @private
     */
    private _slotId: FarmSlotId = null;

    /**
     * TODO: Refactor lazy loading of assets to include token-change detection i.e. "cancelled" promise.
     * @deprecated
     * @private
     */
    private _nextItem: FarmItemId = undefined;

    public selectNextSlot: (_direction: 'left' | 'right') => void;

    public interactable: Interactable;

    /**
     * Keep track of previous parent for z-order override when highlighting slot for catalog preview. This is done because `GameObject.setDisplayParent()` is not working.
     * TODO: Replace with `GameObject.setDisplayParent()` or similar method.
     * @private
     * @deprecated
     */
    public oldParent: GameObject;

    private loadingSpinner: GameObject;

    private itemIdSelector: OutputSelector<IGameState, FarmItemId, (res: FarmItemId) => FarmItemId>;

    private slotSelector: OutputSelector<IGameState, void, (res: FarmItemId, res2: boolean) => void>;

    private previewIdSelector: OutputSelector<
        IGameState,
        void,
        (previewItemId: FarmItemId, isConfirmed: boolean, itemId: FarmItemId) => void
    >;

    private iconSelector: OutputSelector<IGameState, void, (res1: boolean, res2: boolean, res3: FarmItemId) => void>;

    /**
     * Used to adjust ordering of construction effects above all farm slots and slot icons.
     * @private
     */
    private mapLayout: any;

    protected onCreate(params: IFarmSlotData): void {
        super.onCreate(params);

        this._slotId = params.slotId;
        this.selectNextSlot = params.selectNextSlot;

        const { type } = parseFarmSlotId(this.slotID);
        this.setType(type);

        this.mapLayout = params.mapLayout;

        this.interactable = this.getComponentsInChildren(Interactable)[0];
        this.interactable.gameObject.on(GameObjectEvent.SELECTED, this.onSlotClicked);

        this.itemIdSelector = createSelector(
            (state: IGameState) => {
                const itemId = state.playerState.farmState.slots[this.slotID]?.itemId;
                return isFarmItemId(itemId) ? itemId : null;
            },
            itemId => itemId
        );

        const isConfirmedSelector = createSelector(
            (state: IGameState) => state.farmState.previewSlots[this.slotID]?.isConfirmed,
            isConfirmed => isConfirmed
        );

        this.previewIdSelector = createSelector(
            state => state.farmState.previewSlots[this.slotID]?.previewItemId,
            isConfirmedSelector,
            this.itemIdSelector,
            (previewItemId, isConfirmed, itemId) => {
                if (previewItemId !== undefined) {
                    // Show the next preview item.
                    this.loadSlotItem(previewItemId, false);
                } else {
                    // Else the preview is not active.
                    // `isConfirmed` is false when canceling the preview. In this case the slot confirm animation should not show.
                    if (!isConfirmed) {
                        this.loadSlotItem(itemId, false);
                    }
                }
            }
        );

        this.slotSelector = createSelector(this.itemIdSelector, isConfirmedSelector, (itemId, isConfirmed) => {
            // Only animate the slot item change if it has been confirmed i.e. the preview is deactivated at the same time the item changes.
            // This also prevents the animation from playing when loading the scene for the first time.
            if (isConfirmed) {
                this.loadSlotItem(itemId, true);
            }
        });

        const activeSelector = createSelector(
            (state: IGameState) => state.UIState.popupID === PopupID.FARM_CATALOGUE,
            state => state.UIState.params?.slotId === this.slotID,
            (popup, slotId) => {
                return popup && slotId;
            }
        );

        const constructionModeSelector = createSelector(
            (state: IGameState) => state.farmState.constructionModeEnabled,
            constructionMode => constructionMode
        );

        this.iconSelector = createSelector(
            activeSelector,
            constructionModeSelector,
            this.itemIdSelector,
            (active, constructionMode, itemId) => {
                const taskIconRenderable = this.layout.taskIcon.getComponent(Renderable);
                const replyTaskIconRenderable = this.layout.replyTaskIcon.getComponent(Renderable);
                taskIconRenderable.visible = !active && constructionMode && !itemId;
                replyTaskIconRenderable.visible = !active && constructionMode && itemId;
            }
        );

        this.handleStateChanged(Game.store.getState());
    }

    private setLoader(active: boolean) {
        // Show/hide spinner.
        this.loadingSpinner.container.visible = active;

        // Show/hide item.`
        this.layout.itemContainer.container.visible = !active;

        StopAnimation(this.loadingSpinner);

        if (active) {
            // Start spinner.
            this.loadingSpinner.container.rotation = 0;
            Anim(this.loadingSpinner, {
                duration: 700,
                angle: 360,
                delay: 0,
                repeatCount: Infinity,
            });
        }
    }

    private setType(type: FarmSlotType): void {
        this.type = type;

        this.layout = new FarmSlotTypeLayoutMap[this.type]();
        this.addChild(this.layout.root);

        this.loadingSpinner = GameObject.Instantiate(Sprite, {
            texture: 'loading_farm_icon',
            anchor: new Point(0.5, 0.5),
        });

        this.layout.root.addChild(this.loadingSpinner);
        this.loadingSpinner.container.visible = false;
    }

    private async loadSlotItem(itemId: FarmItemId, animate: boolean) {
        // Handle empty slot.
        if (itemId === null) {
            this.setLoader(false);
            this.setItemLayout(null, animate);
            return;
        }

        const assetDetails = getFullFarmItemAssetDetails(itemId);

        // Already loaded - set immediately.
        if (assetDetails.isLoaded()) {
            this.setLoader(false);
            this.setItemLayout(itemId, animate);
            return;
        }

        // Start loading.
        this.setLoader(true);
        this._nextItem = itemId;
        await assetDetails.preload();

        // Check if requested item has changed since loading previous item.
        if (this._nextItem !== itemId) return;

        this.setLoader(false);
        this.setItemLayout(itemId, animate);
    }

    private setItemLayout(itemId: FarmItemId, animate: boolean) {
        if (!itemId) {
            if (animate) {
                this.animateItemClear();
            } else {
                // Hide existing layout.
                this.layout.itemContainer.children.forEach((child: any) => (child.container.visible = false));
            }
        } else {
            // Clear existing layout.
            this.layout.itemContainer.children.forEach((child: any) => child.destroy());

            this.itemLayout = getFarmItemLayout(itemId);
            this.layout.itemContainer.addChild(this.itemLayout.root);

            if (animate) {
                this.animateItemConfirm(itemId);
            }
        }
    }

    private animateItemClear() {
        if (this.layout.itemContainer.children.length === 0) return;

        this.layout.itemContainer.children.forEach((child: any) => (child.container.visible = true));

        const itemArmature = GameObject.Instantiate(Armature, {
            name: `itemArmature_${this.itemId}`,
            dragonbonesName: 'my_farm_slot_animations',
            armatureName: 'remove_item',
            animationName: 'animation',
            playOnStart: false,
            alpha: 0,
        });
        this.layout.root.addChild(itemArmature);
        StartAnimationClip(
            new DragonbonesFadeIn(itemArmature, {
                playTimes: 1,
                concat: false,
            })
        );
        Anim(this.layout.itemContainer.children[0], {
            scaleX: 0,
            scaleY: 0,
            duration: 300,
            delay: 0,
            easing: Easing.Back.Out,
        }).then(() => {
            this.itemLayout?.root.destroy();
            this.itemLayout = null;
            itemArmature.destroy();
        });
    }

    private animateItemConfirm(itemId: FarmItemId) {
        const item = getFarmItemByID(itemId);

        const itemArmature = GameObject.Instantiate(Armature, {
            name: `itemArmature_${this.itemId}`,
            dragonbonesName: 'my_farm_slot_animations',
            armatureName: getSlotArmatureName(this.itemId),
            animationName: 'animation',
            playOnStart: false,
            alpha: 0,
            x: item.farmItemType === FarmItemType.HOUSE ? 50 : 0,
        });
        this.layout.root.addChild(itemArmature);
        this.mapLayout.farmEffects.addChild(itemArmature);

        StartAnimationClip(
            new DragonbonesFadeIn(itemArmature, {
                playTimes: 1,
                concat: false,
            })
        );

        const constructionEffectFarmItemTypes = [
            FarmItemType.HOUSE,
            FarmItemType.FENCE,
            FarmItemType.SMALL,
            FarmItemType.BIG,
        ];

        if (this.itemLayout && !constructionEffectFarmItemTypes.includes(item.farmItemType)) {
            this.itemLayout.item.container.scale.set(0, 0);

            Anim(this.itemLayout.item, {
                scaleX: 1,
                scaleY: 1,
                duration: 400,
                delay: item.slotType === FarmSlotType.HOUSE ? 600 : 200,
                easing: Easing.Back.Out,
            });
        }
    }

    public animatePurchase(index: number) {
        this.layout.slot.getComponent(Renderable).alpha = 0;
        this.layout.taskIcon.getComponent(TransformComponent).position.y -= 50;
        this.layout.taskIcon.getComponent(Renderable).alpha = 0;
        const slotArmature = GameObject.Instantiate(Armature, {
            name: `itemArmature_${this.itemId}`,
            dragonbonesName: 'my_farm_slot_animations',
            armatureName: `unlock_${this.type}_slot`,
            animationName: 'animation',
            playOnStart: false,
            alpha: 0,
        });
        this.layout.root.addChild(slotArmature);

        StartAnimationClip(
            new DragonbonesFadeIn(slotArmature, {
                playTimes: 1,
                concat: false,
                delay: 100 * index,
            })
        );

        Anim(this.layout.slot, {
            alpha: 1,
            duration: 300,
            delay: 400 + 100 * index,
        });

        Anim(this.layout.taskIcon, {
            y: '+50',
            alpha: 1,
            duration: 400,
            delay: 500 + 60 * index,
        });
    }

    /**
     * Handle the slot icons to be at the top of everything else in the farm map.
     * @param farmUI Container inside the farm map layout above the farm map container.
     */
    public handleIcons(farmUI: GameObject) {
        farmUI.addChild(this.layout.taskIcon);
        farmUI.addChild(this.layout.replyTaskIcon);
    }

    public readonly onSlotClicked = () => {
        const state = Game.store.getState();

        const openSlotAudio =
            this.type === FarmSlotType.BIG || FarmSlotType.SMALL
                ? 'open_slot_decoration'
                : this.type === FarmSlotType.CROP
                ? `open_slot_${FarmSlotType.CROP}`
                : `open_slot_${this.type}`;

        PlaySound(openSlotAudio);

        // TODO: Replace following actions with single `FarmActions.selectSlot(slotId)` dispatch and handle the UI in the reducer.
        // If popup already open update with new slotId
        if (state.UIState.popupID === PopupID.FARM_CATALOGUE) {
            UIActions.updateParams(state.UIState.id, PopupID.FARM_CATALOGUE, {
                slotId: this.slotID,
            });
        } else {
            // Else show popup
            UIActions.showPopup(PopupID.FARM_CATALOGUE, {
                slotId: this.slotID,
            });
        }
    };

    public handleStateChanged(state: IGameState): void {
        if (this.destroyed) {
            return;
        }

        this.slotSelector(state);
        this.previewIdSelector(state);
        this.iconSelector(state);
    }

    public get slotID(): FarmSlotId {
        return this._slotId;
    }

    public get itemId(): FarmItemId {
        return this.itemIdSelector(Game.store.getState());
    }
}
